/**
 * VG Help Center — search filtering, live answer counts, popular-question jumps,
 * single-open accordion behaviour and the scroll-spy topic sidebar.
 *
 * The open/close animation itself belongs to the theme's <details-disclosure>
 * (main.js); this only drives it, so every question stays usable with JS
 * disabled — they are plain <details> elements until this upgrades them.
 */
class VgHelpCenter extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.groups = Array.from(this.querySelectorAll('[data-topic-group]')).map((el) => ({
      el,
      topicId: el.dataset.topicId || null,
      countEl: el.querySelector('[data-group-count]'),
      items: Array.from(el.querySelectorAll('.vg-help__item'))
    }));

    this.items = Array.from(this.querySelectorAll('.vg-help__item')).map((el) => ({
      el,
      id: el.dataset.questionId,
      disclosure: el.querySelector('details-disclosure'),
      details: el.querySelector('details'),
      text: el.textContent.toLowerCase().replace(/\s+/g, ' ').trim()
    }));

    this.searchInput = this.querySelector('.vg-help__search-input');
    this.clearButton = this.querySelector('.vg-help__search-clear');
    this.countEl = this.querySelector('.vg-help__search-count');
    this.noResults = this.querySelector('.vg-help__no-results');
    this.popular = this.querySelector('.vg-help__popular');
    this.popularLinks = Array.from(this.querySelectorAll('[data-target-question]'));
    this.navLinks = Array.from(this.querySelectorAll('.vg-help__nav-link'));
    this.quickLinks = Array.from(this.querySelectorAll('.vg-help__quick-link'));

    this.closeOthers = this.dataset.closeOthers === 'true';
    this.countSingular = this.dataset.countSingular || '[count] answer';
    this.countPlural = this.dataset.countPlural || '[count] answers';
    this.resultSingular = this.dataset.resultSingular || '[count] result';
    this.resultPlural = this.dataset.resultPlural || '[count] results';

    this.addListeners();
    this.watchGroups();
    this.watchStuckCloses();
    this.openFromHash();
  }

  disconnectedCallback() {
    if (this.groupObserver) this.groupObserver.disconnect();
    if (this.classObserver) this.classObserver.disconnect();
    window.removeEventListener('hashchange', this.boundOpenFromHash);
  }

  addListeners() {
    if (this.searchInput) {
      const onInput = this.handleSearch.bind(this);
      this.searchInput.addEventListener(
        'input',
        typeof debounce === 'function' ? debounce(onInput, 180) : onInput
      );
      // Enter would submit an enclosing form (the header search wraps some
      // layouts) and reload the page mid-filter.
      this.searchInput.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') evt.preventDefault();
        if (evt.key === 'Escape') this.clearSearch();
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.clearSearch.bind(this));
    }

    // 'toggle' does not bubble, so it has to be bound per <details>.
    this.items.forEach(({ details }) => {
      if (details) details.addEventListener('toggle', this.handleItemToggle.bind(this));
    });

    this.popularLinks.forEach((link) => {
      link.addEventListener('click', this.handlePopularClick.bind(this));
    });

    this.boundOpenFromHash = this.openFromHash.bind(this);
    window.addEventListener('hashchange', this.boundOpenFromHash);
  }

  /* Search ---------------------------------------------------------------- */

  handleSearch() {
    const query = this.searchInput.value.trim().toLowerCase();
    const terms = query.split(/\s+/).filter(Boolean);

    if (this.clearButton) this.clearButton.hidden = terms.length === 0;

    let matches = 0;

    this.items.forEach((item) => {
      const isMatch = terms.length === 0 || terms.every((term) => item.text.includes(term));
      item.el.hidden = !isMatch;
      if (isMatch) matches += 1;

      // A filtered-out answer left open would reopen mid-list when cleared.
      if (!isMatch && this.isOpen(item)) this.closeItem(item);
    });

    this.updateGroupVisibility(terms.length > 0);
    this.updateSearchCount(terms.length > 0, matches);

    if (this.popular) this.popular.hidden = terms.length > 0;
    if (this.noResults) this.noResults.hidden = matches > 0 || terms.length === 0;
  }

  clearSearch() {
    if (!this.searchInput) return;
    this.searchInput.value = '';
    this.handleSearch();
    this.searchInput.focus();
  }

  /**
   * Hides groups with nothing left in them and rewrites every visible count so
   * the sidebar and topic headings describe what is actually on screen.
   */
  updateGroupVisibility(isFiltering) {
    const visibleByTopic = {};

    this.groups.forEach((group) => {
      const visible = group.items.filter((item) => !item.hidden).length;
      group.el.hidden = isFiltering && visible === 0;

      if (group.topicId) visibleByTopic[group.topicId] = visible;
      if (group.countEl) group.countEl.textContent = this.formatCount(visible);
    });

    this.navLinks.forEach((link) => {
      const count = visibleByTopic[link.dataset.topicId];
      if (count === undefined) return;

      const countEl = link.querySelector('[data-nav-count]');
      if (countEl) countEl.textContent = count;

      // Keep empty topics in the DOM but out of the tab order while filtering.
      const isEmpty = isFiltering && count === 0;
      link.closest('li').hidden = isEmpty;
    });
  }

  updateSearchCount(isFiltering, matches) {
    if (!this.countEl) return;
    this.countEl.textContent = isFiltering ? this.formatResults(matches) : '';
  }

  formatCount(count) {
    const template = count === 1 ? this.countSingular : this.countPlural;
    return template.replace('[count]', count);
  }

  formatResults(count) {
    const template = count === 1 ? this.resultSingular : this.resultPlural;
    return template.replace('[count]', count);
  }

  /* Accordion ------------------------------------------------------------- */

  handleItemToggle(evt) {
    const details = evt.target;
    if (!this.closeOthers || !details.open || details.classList.contains('is-closing')) return;

    // 'toggle' fires from inside <details-disclosure>.open(), before it has set
    // the opening panel's height. Closing siblings in that same frame leaves
    // their height transition with nothing to interpolate from, so the
    // 'transitionend' that finishes the close never arrives. Wait for the
    // browser to settle the styles first.
    requestAnimationFrame(() => {
      this.items.forEach((item) => {
        if (item.details === details) return;
        if (this.isOpen(item)) this.closeItem(item);
      });
    });
  }

  /**
   * The theme's <details-disclosure> keeps a details element open while its
   * close transition runs, so 'open' alone is not the whole answer.
   */
  isOpen(item) {
    return Boolean(item.details && item.details.open && !item.details.classList.contains('is-closing'));
  }

  openItem(item) {
    if (!item.details || this.isOpen(item)) return;

    if (item.disclosure && typeof item.disclosure.open === 'function') {
      item.disclosure.open();
    } else {
      item.details.open = true;
    }
  }

  closeItem(item) {
    if (!item.details) return;

    if (item.disclosure && typeof item.disclosure.close === 'function') {
      item.disclosure.close();
    } else {
      item.details.open = false;
    }
  }

  /**
   * A popular-question card is a plain anchor, so it works without JS. With JS
   * it also opens the answer it lands on, which the default anchor jump can't.
   */
  handlePopularClick(evt) {
    const id = evt.currentTarget.dataset.targetQuestion;
    const item = this.items.find((entry) => entry.id === id);
    if (!item) return;

    evt.preventDefault();
    this.revealItem(item, true);

    // Reflect the jump in the URL without a second, un-animated scroll.
    if (window.history.replaceState) {
      window.history.replaceState(null, '', `#${item.el.id}`);
    }
  }

  openFromHash() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const item = this.items.find((entry) => entry.el.id === hash);
    if (item) {
      this.revealItem(item, false);
      return;
    }

    // A topic anchor: let the browser handle the scroll, just don't leave it
    // hidden behind an active search.
    const group = this.groups.find((entry) => entry.el.id === hash);
    if (group && group.el.hidden) this.clearSearch();
  }

  revealItem(item, shouldScroll) {
    if (item.el.hidden) this.clearSearch();

    this.openItem(item);

    if (!shouldScroll) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    const summary = item.el.querySelector('summary');
    if (summary) summary.focus({ preventScroll: true });
  }

  /* Close safety net -------------------------------------------------------- */

  /**
   * <details-disclosure> finishes a close on the panel's 'transitionend', and
   * leaves 'is-closing' set until then. If the height it transitions away from
   * was never committed to style — a close starting in the same frame the panel
   * height was set — no transition runs, the event never arrives, and the row
   * is stuck half-closed and refuses to reopen. Watch for that and finish it.
   */
  watchStuckCloses() {
    if (!('MutationObserver' in window)) return;

    this.closeGuards = new WeakMap();

    this.classObserver = new MutationObserver((records) => {
      records.forEach(({ target }) => {
        clearTimeout(this.closeGuards.get(target));
        if (!target.classList.contains('is-closing')) return;

        // Comfortably past the 0.3s panel transition.
        this.closeGuards.set(target, setTimeout(() => this.finishClose(target), 600));
      });
    });

    this.items.forEach(({ details }) => {
      if (details) {
        this.classObserver.observe(details, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }

  finishClose(details) {
    if (!details.classList.contains('is-closing')) return;

    details.classList.remove('is-closing');
    details.open = false;

    const panel = details.querySelector('.disclosure__panel');
    if (panel) panel.removeAttribute('style');
  }

  /* Sidebar scroll spy ----------------------------------------------------- */

  watchGroups() {
    if (!this.navLinks.length || !('IntersectionObserver' in window)) return;

    const spied = this.groups.filter((group) => group.topicId);
    if (!spied.length) return;

    this.visibleTopics = new Set();

    this.groupObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.dataset.topicId;
          if (entry.isIntersecting) {
            this.visibleTopics.add(id);
          } else {
            this.visibleTopics.delete(id);
          }
        });

        // Mark the topmost group currently in view, so the marker doesn't jump
        // around when several groups straddle the viewport.
        const current = spied.find((group) => this.visibleTopics.has(group.topicId));
        this.navLinks.forEach((link) => {
          const isCurrent = Boolean(current) && link.dataset.topicId === current.topicId;
          link.setAttribute('aria-current', isCurrent ? 'true' : 'false');
        });
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: 0 }
    );

    spied.forEach((group) => this.groupObserver.observe(group.el));
  }
}

customElements.define('vg-help-center', VgHelpCenter);
